import { Helmet } from 'react-helmet-async';
import { useLang } from '../i18n/LanguageContext';

const SITE_URL = 'https://daakyicloud.com';
const SITE_NAME = 'DAAKYI Cloud';
const DEFAULT_IMAGE = `${SITE_URL}/og/default.jpg`;

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/daakyi-logo-full.png`,
  description: 'Pan-African sovereign cloud platform. Secure compute, storage, networking, backup and cybersecurity — engineered by Africans, for Africa, deployable in any jurisdiction.',
  contactPoint: [{
    '@type': 'ContactPoint',
    telephone: '+1-888-853-1350',
    email: 'cloud@daakyi.com',
    contactType: 'sales',
    availableLanguage: ['English', 'French']
  }],
  address: [
    { '@type': 'PostalAddress', streetAddress: '14 Griddle Street, Teshie-Nungua', addressLocality: 'Accra', addressCountry: 'GH' },
    { '@type': 'PostalAddress', streetAddress: '4606 FM 1960 Suite 211', addressLocality: 'Houston', addressRegion: 'TX', postalCode: '77069', addressCountry: 'US' },
    { '@type': 'PostalAddress', streetAddress: 'Rolex Towers, 9th Floor, Sheikh Zayed Road', addressLocality: 'Dubai', addressCountry: 'AE' }
  ],
  sameAs: ['https://daakyi.com']
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: ['en', 'fr']
};

const buildBreadcrumbs = (path) => {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }];
  let acc = '';
  parts.forEach((p, i) => {
    acc += `/${p}`;
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      name: p.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      item: `${SITE_URL}${acc}`
    });
  });
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
};

const GSC_TOKEN = process.env.REACT_APP_GSC_VERIFICATION;

const resolveOgImage = (image) => {
  if (!image) return DEFAULT_IMAGE;
  return image.startsWith('/') ? `${SITE_URL}${image}` : image;
};

// JSON-LD safe serializer — prevents </script> breakout (script-context injection)
const jsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

export const SEO = ({ title, description, path = '/', image, type = 'website', noindex = false, schema, lang, alternates }) => {
  const { lang: siteLang } = useLang();
  const effLang = lang || siteLang || 'en';
  const fullTitle = title ? `${title} – ${SITE_NAME}` : `${SITE_NAME} – Secure African Cloud Infrastructure`;
  const fullUrl = `${SITE_URL}${path}`;
  const ogImage = resolveOgImage(image);
  const breadcrumbs = buildBreadcrumbs(path);

  return (
    <Helmet>
      <html lang={effLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {GSC_TOKEN && <meta name="google-site-verification" content={GSC_TOKEN} />}

      {/* hreflang alternates */}
      {alternates && alternates.map(a => (
        <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={`${SITE_URL}${a.path}`} />
      ))}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={effLang === 'fr' ? 'fr_FR' : 'en_US'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">{jsonLd(ORG_SCHEMA)}</script>
      <script type="application/ld+json">{jsonLd(WEBSITE_SCHEMA)}</script>
      {breadcrumbs && <script type="application/ld+json">{jsonLd(breadcrumbs)}</script>}
      {schema && <script type="application/ld+json">{jsonLd(schema)}</script>}
    </Helmet>
  );
};

export default SEO;
